import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import UserProfile from "users/information/profile-information/user-profile";
import UserProfileSchema from "users/information/profile-information/user-profile-schema";
import { z } from "zod";

export type NetworkInvitationInfoJSON = z.infer<typeof NetworkInvitationInfoSchema.NetworkInvitationInfoJSONZ>;
export type NetworkInvitationInfoParams = z.infer<typeof NetworkInvitationInfoSchema.NetworkInvitationInfoZ>;

export default class NetworkInvitationInfoSchema {
  public static readonly NetworkInvitationInfoJSONZ = z.object({
    id: z.uuid(),
    status: z.string(),
    isInviter: z.boolean(),
    individualId: z.uuid(),
    avatarId: z.uuid().nullish(),
    invitee: UserProfileSchema.UserProfileJSONZ,
    inviter: UserProfileSchema.UserProfileJSONZ,
    created: z.iso.datetime({ offset: true }),
    message: z.string().nullish(),
  });

  public static readonly NetworkInvitationInfoZ = z.object({
    id: z.instanceof(Guid),
    status: z.string(),
    isInviter: z.boolean(),
    individualId: z.instanceof(Guid),
    avatarId: z.instanceof(Guid).optional(),
    invitee: z.instanceof(UserProfile),
    inviter: z.instanceof(UserProfile),
    created: z.instanceof(AHDate),
    message: z.string().optional(),
  });

  public static validate(values: unknown): NetworkInvitationInfoParams {
    return parseOrThrow(NetworkInvitationInfoSchema.NetworkInvitationInfoZ, values, {
      logError: true,
      schemaName: "NetworkInvitationInfoSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): NetworkInvitationInfoJSON {
    return parseOrThrow(NetworkInvitationInfoSchema.NetworkInvitationInfoJSONZ, json, {
      logError: true,
      schemaName: "NetworkInvitationInfoSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is NetworkInvitationInfoParams {
    try {
      NetworkInvitationInfoSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is NetworkInvitationInfoJSON {
    try {
      NetworkInvitationInfoSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: NetworkInvitationInfoJSON): NetworkInvitationInfoParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(NetworkInvitationInfoSchema.NetworkInvitationInfoJSONZ, json, {
      logError: true,
      schemaName: "NetworkInvitationInfoSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return NetworkInvitationInfoSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: NetworkInvitationInfoParams): NetworkInvitationInfoJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = NetworkInvitationInfoSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return NetworkInvitationInfoSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    NetworkInvitationInfoSchema.NetworkInvitationInfoJSONZ,
    NetworkInvitationInfoSchema.NetworkInvitationInfoZ,
    {
      decode: (data) => ({
        id: Guid.fromJSON(data.id)!,
        status: data.status,
        isInviter: data.isInviter,
        individualId: new Guid(GuidSchema.deserialize(data.individualId)),
        avatarId: Guid.fromJSON(data.avatarId) ?? undefined,
        invitee: UserProfile.fromJSON(data.invitee),
        inviter: UserProfile.fromJSON(data.inviter),
        created: new AHDate(DateSchema.deserialize(data.created)),
        message: data.message ?? undefined,
      }),
      encode: (invite) => ({
        id: invite.id.toJSON(),
        status: invite.status,
        isInviter: invite.isInviter,
        individualId: invite.individualId.toJSON(),
        avatarId: invite.avatarId?.toJSON(),
        invitee: invite.invitee.toJSON(),
        inviter: invite.inviter.toJSON(),
        created: invite.created.toJSON(),
        message: invite.message,
      }),
    }
  );
}
